import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Save, 
  X, 
  ExternalLink,
  ChevronRight,
  Search,
  Building2,
  Package,
  CheckCircle2,
  Shield,
  BarChart3,
  Calendar,
  Trash2,
  Users,
  User,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Types
type ComplianceRegimeCode = 'PUWER' | 'LOLER' | 'PAT' | 'EICR' | 'MOT' | 'GDPR' | 'ISO9001' | 'ISO14001' | 'ISO27001' | 'ISO45001';
type IntervalUnit = 'days' | 'months' | 'years';
type AssignmentType = 'asset_owner' | 'team' | 'user';

interface ComplianceRegime {
  code: ComplianceRegimeCode;
  name: string;
  description: string;
  category: string;
}

interface RegimeSettings {
  enabled: boolean;
  defaultInterval: number;
  intervalUnit: IntervalUnit;
  gracePeriod: number;
  gracePeriodUnit: IntervalUnit;
  minAssetValue: number | null;
  maxAssetValue: number | null;
  regulatoryBody: string;
  legislation: string;
  guidanceUrl: string;
  enabledCategoryIds: string[];
  enabledLocationIds: string[];
  requirements: Array<{ id: string; text: string; sortOrder: number }>;
  assignmentType: AssignmentType;
  assignedTeamId: string | null;
  assignedUserId: string | null;
  requiredDocuments: Array<{ id: string; documentType: string; mandatory: boolean; sortOrder: number }>;
  complianceScore: number;
}

interface Category {
  id: string;
  name: string;
  assetCount: number;
  parentId?: string;
}

interface Location {
  id: string;
  name: string;
  address: string;
  assetCount: number;
  parentId?: string;
}

// Mock data
const COMPLIANCE_REGIMES: Record<ComplianceRegimeCode, ComplianceRegime> = {
  PUWER: {
    code: 'PUWER',
    name: 'Work Equipment Safety (PUWER)',
    description: 'Provision and Use of Work Equipment Regulations',
    category: 'Health & Safety',
  },
  LOLER: {
    code: 'LOLER',
    name: 'Lifting Operations (LOLER)',
    description: 'Lifting Operations and Lifting Equipment Regulations',
    category: 'Health & Safety',
  },
  PAT: {
    code: 'PAT',
    name: 'Portable Appliance Testing',
    description: 'Electrical safety testing for portable appliances',
    category: 'Health & Safety',
  },
  EICR: {
    code: 'EICR',
    name: 'Electrical Installation Condition Report',
    description: 'Fixed electrical installation testing',
    category: 'Health & Safety',
  },
  MOT: {
    code: 'MOT',
    name: 'MOT Testing',
    description: 'Ministry of Transport vehicle safety testing',
    category: 'Vehicle Compliance',
  },
  GDPR: {
    code: 'GDPR',
    name: 'Data Protection (GDPR)',
    description: 'General Data Protection Regulation compliance',
    category: 'Data & Privacy',
  },
  ISO9001: {
    code: 'ISO9001',
    name: 'Quality Management (ISO 9001)',
    description: 'Quality management system standard',
    category: 'ISO Standards',
  },
  ISO14001: {
    code: 'ISO14001',
    name: 'Environmental Management (ISO 14001)',
    description: 'Environmental management system standard',
    category: 'ISO Standards',
  },
  ISO27001: {
    code: 'ISO27001',
    name: 'Information Security (ISO 27001)',
    description: 'Information security management standard',
    category: 'ISO Standards',
  },
  ISO45001: {
    code: 'ISO45001',
    name: 'Occupational Health & Safety (ISO 45001)',
    description: 'Occupational health and safety management standard',
    category: 'ISO Standards',
  },
};

const MOCK_CATEGORIES: Category[] = [
  { id: 'cat-1', name: 'Machinery & Equipment', assetCount: 45 },
  { id: 'cat-1-1', name: 'Industrial Machinery', assetCount: 25, parentId: 'cat-1' },
  { id: 'cat-1-2', name: 'Workshop Tools', assetCount: 20, parentId: 'cat-1' },
  { id: 'cat-2', name: 'IT Equipment', assetCount: 120 },
  { id: 'cat-2-1', name: 'Computers & Laptops', assetCount: 80, parentId: 'cat-2' },
  { id: 'cat-2-2', name: 'Servers', assetCount: 15, parentId: 'cat-2' },
  { id: 'cat-2-3', name: 'Peripherals', assetCount: 25, parentId: 'cat-2' },
  { id: 'cat-3', name: 'Vehicles', assetCount: 18 },
  { id: 'cat-3-1', name: 'Company Cars', assetCount: 12, parentId: 'cat-3' },
  { id: 'cat-3-2', name: 'Vans', assetCount: 6, parentId: 'cat-3' },
  { id: 'cat-4', name: 'Furniture & Fixtures', assetCount: 200 },
  { id: 'cat-5', name: 'Lifting Equipment', assetCount: 8 },
];

const MOCK_LOCATIONS: Location[] = [
  { id: 'loc-1', name: 'Head Office', address: '123 Business Park, London, UK', assetCount: 250 },
  { id: 'loc-1-1', name: 'Ground Floor', address: '123 Business Park, London, UK', assetCount: 100, parentId: 'loc-1' },
  { id: 'loc-1-2', name: 'First Floor', address: '123 Business Park, London, UK', assetCount: 80, parentId: 'loc-1' },
  { id: 'loc-1-3', name: 'Second Floor', address: '123 Business Park, London, UK', assetCount: 70, parentId: 'loc-1' },
  { id: 'loc-2', name: 'Manchester Warehouse', address: '45 Industrial Estate, Manchester, UK', assetCount: 85 },
  { id: 'loc-2-1', name: 'Storage Area A', address: '45 Industrial Estate, Manchester, UK', assetCount: 40, parentId: 'loc-2' },
  { id: 'loc-2-2', name: 'Storage Area B', address: '45 Industrial Estate, Manchester, UK', assetCount: 45, parentId: 'loc-2' },
  { id: 'loc-3', name: 'Birmingham Office', address: '78 City Centre, Birmingham, UK', assetCount: 42 },
  { id: 'loc-4', name: 'Remote Sites', address: 'Various Locations', assetCount: 14 },
];

const MOCK_TEAMS = [
  { id: 'team-1', name: 'Maintenance Team' },
  { id: 'team-2', name: 'Facilities Team' },
  { id: 'team-3', name: 'Health & Safety Team' },
  { id: 'team-4', name: 'Operations Team' },
];

const MOCK_USERS = [
  { id: 'user-1', name: 'John Smith' },
  { id: 'user-2', name: 'Sarah Johnson' },
  { id: 'user-3', name: 'Mike Williams' },
];

function getComplianceScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
  if (score >= 60) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-red-600 bg-red-50 border-red-200';
}

export default function ComplianceRegulationsPage(): React.ReactElement {
  const navigate = useNavigate();
  const { modules } = useFeatureSettings();
  const complianceModule = modules.compliance;
  const isDemoMode = complianceModule?.mode === 'demo';
  
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedRegime, setSelectedRegime] = useState<ComplianceRegimeCode>('PUWER');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [categorySearch, setCategorySearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingRegime, setPendingRegime] = useState<ComplianceRegimeCode | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Initialize default settings for all regimes
  const getDefaultSettings = (): Record<ComplianceRegimeCode, RegimeSettings> => {
    const allRegimes = Object.keys(COMPLIANCE_REGIMES) as ComplianceRegimeCode[];
    const settings: Partial<Record<ComplianceRegimeCode, RegimeSettings>> = {};
    
    // Define specific settings for known regimes
    const specificSettings: Partial<Record<ComplianceRegimeCode, RegimeSettings>> = {
      PUWER: {
        enabled: true,
        defaultInterval: 6,
        intervalUnit: 'months',
        gracePeriod: 7,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: 'HSE (Health and Safety Executive)',
        legislation: 'PUWER 1998 reg. 6',
        guidanceUrl: 'https://www.hse.gov.uk/work-equipment-machinery/puwer.htm',
        enabledCategoryIds: ['cat-1', 'cat-1-1', 'cat-1-2', 'cat-5'],
        enabledLocationIds: ['loc-1', 'loc-2', 'loc-3'],
        requirements: [
          { id: 'req-1', text: 'Equipment is suitable for intended use', sortOrder: 1 },
          { id: 'req-2', text: 'Equipment is maintained in safe working order', sortOrder: 2 },
          { id: 'req-3', text: 'Inspection records are up to date', sortOrder: 3 },
        ],
        assignmentType: 'asset_owner',
        assignedTeamId: null,
        assignedUserId: null,
        requiredDocuments: [
          { id: 'doc-1', documentType: 'Inspection Report', mandatory: true, sortOrder: 1 },
          { id: 'doc-2', documentType: 'Photo Evidence', mandatory: false, sortOrder: 2 },
        ],
        complianceScore: 87,
      },
      LOLER: {
        enabled: true,
        defaultInterval: 6,
        intervalUnit: 'months',
        gracePeriod: 3,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: 'HSE (Health and Safety Executive)',
        legislation: 'LOLER 1998',
        guidanceUrl: 'https://www.hse.gov.uk/work-equipment-machinery/loler.htm',
        enabledCategoryIds: ['cat-1', 'cat-1-1', 'cat-1-2'],
        enabledLocationIds: ['loc-1', 'loc-2'],
        requirements: [
          { id: 'req-1', text: 'Lifting equipment is fit for purpose', sortOrder: 1 },
          { id: 'req-2', text: 'Safe working load is clearly marked', sortOrder: 2 },
        ],
        assignmentType: 'team',
        assignedTeamId: 'team-3',
        assignedUserId: null,
        requiredDocuments: [
          { id: 'doc-1', documentType: 'Thorough Examination Report', mandatory: true, sortOrder: 1 },
        ],
        complianceScore: 92,
      },
      PAT: {
        enabled: true,
        defaultInterval: 12,
        intervalUnit: 'months',
        gracePeriod: 14,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: 'HSE (Health and Safety Executive)',
        legislation: 'Electricity at Work Regulations 1989',
        guidanceUrl: 'https://www.hse.gov.uk/electricity/pat-testing.htm',
        enabledCategoryIds: ['cat-2', 'cat-2-1', 'cat-2-3'],
        enabledLocationIds: ['loc-1', 'loc-4'],
        requirements: [
          { id: 'req-1', text: 'Visual inspection completed', sortOrder: 1 },
          { id: 'req-2', text: 'Electrical safety tests passed', sortOrder: 2 },
        ],
        assignmentType: 'user',
        assignedTeamId: null,
        assignedUserId: 'user-2',
        requiredDocuments: [
          { id: 'doc-1', documentType: 'PAT Test Certificate', mandatory: true, sortOrder: 1 },
        ],
        complianceScore: 78,
      },
      EICR: {
        enabled: true,
        defaultInterval: 5,
        intervalUnit: 'years',
        gracePeriod: 30,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: 'NICEIC',
        legislation: 'BS 7671 Wiring Regulations',
        guidanceUrl: 'https://www.niceic.com/electrician/eicr',
        enabledCategoryIds: ['cat-2'],
        enabledLocationIds: ['loc-1', 'loc-2', 'loc-3', 'loc-4'],
        requirements: [
          { id: 'req-1', text: 'Fixed electrical installation inspected', sortOrder: 1 },
        ],
        assignmentType: 'team',
        assignedTeamId: 'team-2',
        assignedUserId: null,
        requiredDocuments: [
          { id: 'doc-1', documentType: 'EICR Certificate', mandatory: true, sortOrder: 1 },
        ],
        complianceScore: 95,
      },
      MOT: {
        enabled: true,
        defaultInterval: 1,
        intervalUnit: 'years',
        gracePeriod: 0,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: 'DVSA (Driver and Vehicle Standards Agency)',
        legislation: 'Road Traffic Act 1988',
        guidanceUrl: 'https://www.gov.uk/getting-an-mot',
        enabledCategoryIds: ['cat-3'],
        enabledLocationIds: ['loc-1', 'loc-2'],
        requirements: [
          { id: 'req-1', text: 'Vehicle roadworthiness verified', sortOrder: 1 },
        ],
        assignmentType: 'asset_owner',
        assignedTeamId: null,
        assignedUserId: null,
        requiredDocuments: [
          { id: 'doc-1', documentType: 'MOT Certificate', mandatory: true, sortOrder: 1 },
        ],
        complianceScore: 100,
      },
    };

    // Create default settings for all regimes
    allRegimes.forEach(regime => {
      settings[regime] = specificSettings[regime] || {
        enabled: false,
        defaultInterval: 12,
        intervalUnit: 'months',
        gracePeriod: 7,
        gracePeriodUnit: 'days',
        minAssetValue: null,
        maxAssetValue: null,
        regulatoryBody: '',
        legislation: '',
        guidanceUrl: '',
        enabledCategoryIds: [],
        enabledLocationIds: [],
        requirements: [],
        assignmentType: 'asset_owner',
        assignedTeamId: null,
        assignedUserId: null,
        requiredDocuments: [],
        complianceScore: 0,
      };
    });

    return settings as Record<ComplianceRegimeCode, RegimeSettings>;
  };

  const [regimeSettings, setRegimeSettings] = useState<Record<ComplianceRegimeCode, RegimeSettings>>(getDefaultSettings());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingName, setEditingName] = useState<string>('');

  const currentSettings = regimeSettings[selectedRegime];
  const currentRegime = COMPLIANCE_REGIMES[selectedRegime];

  const updateSettings = (updates: Partial<RegimeSettings>) => {
    setRegimeSettings(prev => ({
      ...prev,
      [selectedRegime]: { ...prev[selectedRegime], ...updates },
    }));
    setHasChanges(true);
    setIsEditMode(true);
  };

  const handleSave = async () => {
    console.log('Saving settings:', regimeSettings[selectedRegime]);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    setHasChanges(false);
    setIsEditMode(false);
    return true;
  };

  const handleReset = () => {
    setRegimeSettings(getDefaultSettings());
    setHasChanges(false);
  };

  const handleRegimeChange = (regime: ComplianceRegimeCode) => {
    // If in edit mode and has changes, show confirmation dialog
    if (isEditMode && hasChanges) {
      setPendingRegime(regime);
      setShowConfirmDialog(true);
    } else {
      // No changes or not in edit mode, change directly
      setSelectedRegime(regime);
      setIsEditMode(false);
      setHasChanges(false);
    }
  };

  const handleConfirmSave = async () => {
    const success = await handleSave();
    if (success && pendingRegime) {
      setSelectedRegime(pendingRegime);
      setPendingRegime(null);
      setShowConfirmDialog(false);
    }
  };

  const handleDiscardChanges = () => {
    if (pendingRegime) {
      setSelectedRegime(pendingRegime);
      setPendingRegime(null);
    }
    setHasChanges(false);
    setIsEditMode(false);
    setShowConfirmDialog(false);
  };

  const handleCancelChange = () => {
    setPendingRegime(null);
    setShowConfirmDialog(false);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    console.log('Deleting regulation:', selectedRegime);
    // Simulate API call
    setShowDeleteDialog(false);
    // In a real app, you'd navigate away or update the list
  };

  const handleNameChange = (newName: string) => {
    setEditingName(newName);
    setHasChanges(true);
    setIsEditMode(true);
  };

  // Filter categories and locations
  const filteredRegimes = Object.values(COMPLIANCE_REGIMES).filter(regime => {
    const query = searchQuery.toLowerCase();
    return regime.code.toLowerCase().includes(query) || 
           regime.name.toLowerCase().includes(query);
  });

  const filteredCategories = MOCK_CATEGORIES.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const filteredLocations = MOCK_LOCATIONS.filter(loc =>
    loc.name.toLowerCase().includes(locationSearch.toLowerCase()) ||
    loc.address.toLowerCase().includes(locationSearch.toLowerCase())
  );

  const toggleCategory = (categoryId: string) => {
    const newIds = currentSettings.enabledCategoryIds.includes(categoryId)
      ? currentSettings.enabledCategoryIds.filter(id => id !== categoryId)
      : [...currentSettings.enabledCategoryIds, categoryId];
    updateSettings({ enabledCategoryIds: newIds });
  };

  const toggleLocation = (locationId: string) => {
    const newIds = currentSettings.enabledLocationIds.includes(locationId)
      ? currentSettings.enabledLocationIds.filter(id => id !== locationId)
      : [...currentSettings.enabledLocationIds, locationId];
    updateSettings({ enabledLocationIds: newIds });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background flex-col">
      {/* Page Header - Full Width */}
      <div className="bg-card flex-shrink-0">
        <div className="p-6">
          <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                Compliance Management
                {isDemoMode && (
                  <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                    Demo Mode
                  </Badge>
                )}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Physical asset compliance management for UK HSE & British Standards
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs - Full Width */}
      <div className="border-t border-b bg-[rgb(242,245,245)] dark:bg-black flex-shrink-0">
        <div className="px-6 py-2">
          <Tabs value="regulations" className="w-full">
            <TabsList className="h-9 bg-transparent p-0 gap-1">
              <TabsTrigger 
                value="dashboard" 
                onClick={() => navigate('/compliance')}
                className="px-2 py-1.5 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-[rgb(242,245,245)] data-[state=inactive]:dark:bg-black data-[state=inactive]:hover:bg-slate-200 data-[state=inactive]:dark:hover:bg-slate-800"
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger 
                value="assets" 
                onClick={() => navigate('/compliance/assets')}
                className="px-2 py-1.5 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-[rgb(242,245,245)] data-[state=inactive]:dark:bg-black data-[state=inactive]:hover:bg-slate-200 data-[state=inactive]:dark:hover:bg-slate-800"
              >
                <Package className="mr-2 h-4 w-4" />
                Asset Compliance
              </TabsTrigger>
              <TabsTrigger 
                value="regulations" 
                onClick={() => navigate('/compliance/regulations')}
                className="px-2 py-1.5 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-[rgb(242,245,245)] data-[state=inactive]:dark:bg-black data-[state=inactive]:hover:bg-slate-200 data-[state=inactive]:dark:hover:bg-slate-800"
              >
                <Shield className="mr-2 h-4 w-4" />
                Regulations
              </TabsTrigger>
              <TabsTrigger 
                value="schedule" 
                onClick={() => navigate('/compliance/schedule')}
                className="px-2 py-1.5 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-[rgb(242,245,245)] data-[state=inactive]:dark:bg-black data-[state=inactive]:hover:bg-slate-200 data-[state=inactive]:dark:hover:bg-slate-800"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Schedule
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Content Area with Sidebar and Details */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Regulation List */}
        <div className="w-80 bg-muted/30 flex flex-col overflow-hidden border-r-[3px]">
          {/* Search Input */}
          <div className="p-3 border-b bg-card">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by code or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto overflow-hidden">
            <div className="p-3 space-y-2">
              {filteredRegimes.map((regime) => {
                const settings = regimeSettings[regime.code];
                const isSelected = selectedRegime === regime.code;

                return (
                  <button
                    key={regime.code}
                    onClick={() => handleRegimeChange(regime.code)}
                    className={cn(
                      'w-full text-left p-3 rounded-lg border transition-all duration-200',
                      'hover:border-primary/50 hover:bg-accent/50 hover:shadow-md hover:scale-[1.02]',
                      isSelected
                        ? 'bg-primary/10 border-primary/50 ring-1 ring-primary/20'
                        : 'bg-card border-border'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0 space-y-2">
                        {/* Name and Code */}
                        <div>
                          <h3 className={cn(
                            'font-semibold text-sm line-clamp-1',
                            isSelected && 'text-primary'
                          )}>
                            {regime.name}
                          </h3>
                          <p className="text-xs text-muted-foreground font-mono mt-0.5">
                            {regime.code}
                          </p>
                        </div>

                        {/* Description */}
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {regime.description}
                        </p>

                        {/* Status and Inspection Interval */}
                        <div className="flex items-center gap-2 text-xs">
                          <div className="flex items-center gap-1.5">
                            <div className={cn(
                              'w-2 h-2 rounded-full',
                              settings.enabled ? 'bg-green-600' : 'bg-slate-400'
                            )} />
                            <span className="text-muted-foreground">
                              {settings.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                          <Separator orientation="vertical" className="h-3" />
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">
                              {settings.defaultInterval} {settings.intervalUnit}
                            </span>
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <ChevronRight className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Panel - Regulation Details */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Regulation Header */}
          <div className="border-b bg-card px-6 py-4 flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h2 className="text-lg font-semibold mb-1">{currentRegime.name}</h2>
                <p className="text-sm text-muted-foreground font-mono">{currentRegime.code}</p>
              </div>
              <div className="flex items-center gap-2">
                {hasChanges && (
                  <Button variant="outline" size="sm" onClick={handleReset}>
                    <X className="h-4 w-4 mr-1" />
                    Reset
                  </Button>
                )}
                <Button size="sm" onClick={handleSave} disabled={!hasChanges}>
                  <Save className="h-4 w-4 mr-1" />
                  Save Changes
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDelete}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <div className="border-t border-b bg-[rgb(242,245,245)] dark:bg-black flex-shrink-0">
              <div className="px-6 py-2">
                <TabsList className="h-9 bg-transparent p-0 gap-1">
                  <TabsTrigger 
                    value="details" 
                    className="px-2 py-1.5 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-[rgb(242,245,245)] data-[state=inactive]:dark:bg-black data-[state=inactive]:hover:bg-slate-200 data-[state=inactive]:dark:hover:bg-slate-800"
                  >
                    Details
                  </TabsTrigger>
                  <TabsTrigger 
                    value="categories" 
                    className="px-2 py-1.5 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-[rgb(242,245,245)] data-[state=inactive]:dark:bg-black data-[state=inactive]:hover:bg-slate-200 data-[state=inactive]:dark:hover:bg-slate-800"
                  >
                    Categories
                    <Badge variant="secondary" className="ml-2">
                      {currentSettings.enabledCategoryIds.length}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="locations" 
                    className="px-2 py-1.5 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:bg-[rgb(242,245,245)] data-[state=inactive]:dark:bg-black data-[state=inactive]:hover:bg-slate-200 data-[state=inactive]:dark:hover:bg-slate-800"
                  >
                    Locations
                    <Badge variant="secondary" className="ml-2">
                      {currentSettings.enabledLocationIds.length}
                    </Badge>
                  </TabsTrigger>
                </TabsList>
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              <div className="p-6">
              {/* Details Tab */}
              <TabsContent value="details" className="mt-0 space-y-6">
                {/* Basic Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Basic Information</CardTitle>
                    <CardDescription>Core configuration settings for the compliance rule</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Rule Code</Label>
                        <Input value={currentRegime.code} disabled className="bg-muted" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rule-name">Rule Name *</Label>
                        <Input
                          id="rule-name"
                          value={editingName || currentRegime.name}
                          onChange={(e) => handleNameChange(e.target.value)}
                          placeholder="Enter rule name"
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 pt-2">
                      <Switch
                        id="enable-rule"
                        checked={currentSettings.enabled}
                        onCheckedChange={(checked) => updateSettings({ enabled: checked })}
                      />
                      <Label htmlFor="enable-rule" className="font-normal cursor-pointer">
                        Rule Enabled - {currentSettings.enabled ? 'This rule is currently active' : 'This rule is currently inactive'}
                      </Label>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="interval">Default Inspection Interval *</Label>
                        <div className="flex gap-2">
                          <Input
                            id="interval"
                            type="number"
                            value={currentSettings.defaultInterval}
                            onChange={(e) => updateSettings({ defaultInterval: parseInt(e.target.value) || 0 })}
                            className="flex-1"
                          />
                          <Select
                            value={currentSettings.intervalUnit}
                            onValueChange={(value: IntervalUnit) => updateSettings({ intervalUnit: value })}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="days">Days</SelectItem>
                              <SelectItem value="months">Months</SelectItem>
                              <SelectItem value="years">Years</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="grace">Grace Period</Label>
                        <div className="flex gap-2">
                          <Input
                            id="grace"
                            type="number"
                            value={currentSettings.gracePeriod}
                            onChange={(e) => updateSettings({ gracePeriod: parseInt(e.target.value) || 0 })}
                            className="flex-1"
                          />
                          <Select
                            value={currentSettings.gracePeriodUnit}
                            onValueChange={(value: IntervalUnit) => updateSettings({ gracePeriodUnit: value })}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="days">Days</SelectItem>
                              <SelectItem value="months">Months</SelectItem>
                              <SelectItem value="years">Years</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="minValue">Minimum Asset Value</Label>
                        <Input
                          id="minValue"
                          type="number"
                          placeholder="No minimum"
                          value={currentSettings.minAssetValue ?? ''}
                          onChange={(e) => updateSettings({ minAssetValue: e.target.value ? parseFloat(e.target.value) : null })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="maxValue">Maximum Asset Value</Label>
                        <Input
                          id="maxValue"
                          type="number"
                          placeholder="No maximum"
                          value={currentSettings.maxAssetValue ?? ''}
                          onChange={(e) => updateSettings({ maxAssetValue: e.target.value ? parseFloat(e.target.value) : null })}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Regulation */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Regulation</CardTitle>
                    <CardDescription>Reference information linking to official regulations</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="regBody">Regulatory Body</Label>
                      <Input
                        id="regBody"
                        value={currentSettings.regulatoryBody}
                        onChange={(e) => updateSettings({ regulatoryBody: e.target.value })}
                        placeholder="e.g., HSE (Health and Safety Executive)"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="legislation">Legislation</Label>
                      <Input
                        id="legislation"
                        value={currentSettings.legislation}
                        onChange={(e) => updateSettings({ legislation: e.target.value })}
                        placeholder="e.g., PUWER 1998 reg. 6"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="guidanceUrl">Guidance URL</Label>
                      <div className="flex gap-2">
                        <Input
                          id="guidanceUrl"
                          value={currentSettings.guidanceUrl}
                          onChange={(e) => updateSettings({ guidanceUrl: e.target.value })}
                          placeholder="https://..."
                          className="flex-1"
                        />
                        {currentSettings.guidanceUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(currentSettings.guidanceUrl, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Open
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Default Assignment */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Default Assignment</CardTitle>
                    <CardDescription>Determines who receives compliance tasks</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Asset Owner Option */}
                    <div
                      className={cn(
                        "relative flex cursor-pointer rounded-lg border-2 p-4 transition-all",
                        currentSettings.assignmentType === 'asset_owner'
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50 hover:bg-accent/50"
                      )}
                      onClick={() => updateSettings({ assignmentType: 'asset_owner' })}
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <div className={cn(
                          "mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center",
                          currentSettings.assignmentType === 'asset_owner'
                            ? "border-primary"
                            : "border-muted-foreground"
                        )}>
                          {currentSettings.assignmentType === 'asset_owner' && (
                            <div className="h-2 w-2 rounded-full bg-primary" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <Label className="font-semibold cursor-pointer">Asset Owner</Label>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            Tasks are automatically assigned to the designated owner of each asset
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Specific Team Option */}
                    <div
                      className={cn(
                        "relative flex flex-col cursor-pointer rounded-lg border-2 p-4 transition-all",
                        currentSettings.assignmentType === 'team'
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50 hover:bg-accent/50"
                      )}
                      onClick={() => updateSettings({ assignmentType: 'team' })}
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center",
                          currentSettings.assignmentType === 'team'
                            ? "border-primary"
                            : "border-muted-foreground"
                        )}>
                          {currentSettings.assignmentType === 'team' && (
                            <div className="h-2 w-2 rounded-full bg-primary" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <Label className="font-semibold cursor-pointer">Specific Team</Label>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            All tasks are assigned to a designated team
                          </p>
                        </div>
                      </div>
                      {currentSettings.assignmentType === 'team' && (
                        <div className="ml-7 mt-3" onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={currentSettings.assignedTeamId ?? ''}
                            onValueChange={(value: string) => updateSettings({ assignedTeamId: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select team..." />
                            </SelectTrigger>
                            <SelectContent>
                              {MOCK_TEAMS.map((team) => (
                                <SelectItem key={team.id} value={team.id}>
                                  {team.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Specific User Option */}
                    <div
                      className={cn(
                        "relative flex flex-col cursor-pointer rounded-lg border-2 p-4 transition-all",
                        currentSettings.assignmentType === 'user'
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50 hover:bg-accent/50"
                      )}
                      onClick={() => updateSettings({ assignmentType: 'user' })}
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center",
                          currentSettings.assignmentType === 'user'
                            ? "border-primary"
                            : "border-muted-foreground"
                        )}>
                          {currentSettings.assignmentType === 'user' && (
                            <div className="h-2 w-2 rounded-full bg-primary" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <Label className="font-semibold cursor-pointer">Specific User</Label>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            All tasks are assigned to a specific individual
                          </p>
                        </div>
                      </div>
                      {currentSettings.assignmentType === 'user' && (
                        <div className="ml-7 mt-3" onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={currentSettings.assignedUserId ?? ''}
                            onValueChange={(value: string) => updateSettings({ assignedUserId: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select user..." />
                            </SelectTrigger>
                            <SelectContent>
                              {MOCK_USERS.map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                  {user.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Categories Tab */}
              <TabsContent value="categories" className="mt-0">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Package className="h-5 w-5" />
                          Asset Categories
                        </CardTitle>
                        <CardDescription>
                          Select which asset categories this compliance rule applies to
                        </CardDescription>
                      </div>
                      <Badge variant="secondary" className="text-sm">
                        {currentSettings.enabledCategoryIds.length} selected
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search categories..."
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          className="pl-9"
                        />
                      </div>

                      <Separator />

                      <div className="space-y-1">
                        {filteredCategories.map((category) => {
                          const isEnabled = currentSettings.enabledCategoryIds.includes(category.id);
                          const isParent = !category.parentId;
                          const isChild = !!category.parentId;

                          return (
                            <div
                              key={category.id}
                              className={cn(
                                'flex items-center justify-between p-3 rounded-lg border transition-all',
                                isEnabled ? 'bg-primary/5 border-primary/20' : 'bg-card border-border hover:bg-accent',
                                isChild && 'ml-6'
                              )}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={() => toggleCategory(category.id)}
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className={cn('text-sm font-medium', isParent && 'font-semibold')}>
                                      {category.name}
                                    </span>
                                    {isEnabled && (
                                      <CheckCircle2 className="h-4 w-4 text-primary" />
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Badge variant="outline" className="ml-2">
                                {category.assetCount} assets
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Locations Tab */}
              <TabsContent value="locations" className="mt-0">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Building2 className="h-5 w-5" />
                          Locations
                        </CardTitle>
                        <CardDescription>
                          Select which locations this compliance rule applies to
                        </CardDescription>
                      </div>
                      <Badge variant="secondary" className="text-sm">
                        {currentSettings.enabledLocationIds.length} selected
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search locations..."
                          value={locationSearch}
                          onChange={(e) => setLocationSearch(e.target.value)}
                          className="pl-9"
                        />
                      </div>

                      <Separator />

                      <div className="space-y-1">
                        {filteredLocations.map((location) => {
                          const isEnabled = currentSettings.enabledLocationIds.includes(location.id);
                          const isParent = !location.parentId;
                          const isChild = !!location.parentId;

                          return (
                            <div
                              key={location.id}
                              className={cn(
                                'flex items-center justify-between p-3 rounded-lg border transition-all',
                                isEnabled ? 'bg-primary/5 border-primary/20' : 'bg-card border-border hover:bg-accent',
                                isChild && 'ml-6'
                              )}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={() => toggleLocation(location.id)}
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={cn('text-sm font-medium', isParent && 'font-semibold')}>
                                      {location.name}
                                    </span>
                                    {isEnabled && (
                                      <CheckCircle2 className="h-4 w-4 text-primary" />
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground">{location.address}</p>
                                </div>
                              </div>
                              <Badge variant="outline" className="ml-2">
                                {location.assetCount} assets
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              </div>
            </div>
        </Tabs>
      </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Would you like to save them before switching to another regulation?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancelChange}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDiscardChanges}>
              Discard Changes
            </Button>
            <Button onClick={handleConfirmSave}>
              Save & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Regulation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{currentRegime.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Regulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
